import { FMDPStorage } from "../../shared/helper";
export const FMDP_APPLICATIONS = ["ManagementPortal", "SuperAdmin", "Tracking", "EcoDriveDriverScore"];

export const groupedUserFeatures = (app_features) => {
  const groupedData = {};
  for (let i = 0; i < app_features?.length; i++) {
    const { name, id, label_name } = app_features[i];

    if (groupedData[name]) {
      groupedData[name][label_name] = id;
    } else {
      groupedData[name] = { [label_name]: id };
    }
  }
  return groupedData;
};

export const getRoleIdsForSelectedApp = (
  userRoles = [],
  selectedAppId,
  selectedAppRoleIds = [],
  clientIds = []
) => {
  if (!Array.isArray(userRoles)) return [];

  const rolePairs = userRoles.filter((role) => Array.isArray(role) && role.length === 2);
  if (rolePairs.length > 0) {
    return rolePairs
      .filter(([clientAppId]) => String(clientAppId) === String(selectedAppId))
      .map(([, roleId]) => roleId);
  }

  // Some users API responses return flat role IDs and a parallel client_ids
  // array, e.g. client_ids [1, 509] and roles [2, 1320]. Preserve that
  // association instead of matching against another client's role catalog.
  const clientIndex = clientIds.findIndex(
    (clientId) => String(clientId) === String(selectedAppId)
  );
  if (clientIndex >= 0 && clientIndex < userRoles.length) {
    return [userRoles[clientIndex]];
  }

  const availableRoleIds = new Set(selectedAppRoleIds.map((roleId) => String(roleId)));
  return userRoles.filter(
    (roleId) => !Array.isArray(roleId) && availableRoleIds.has(String(roleId))
  );
};

/**
 * Build the user's client_app_roles for the selected app by fetching the
 * full roles list from the API and matching against the user's assigned
 * role IDs (from the SSO user object's `roles` field).
 *
 * The SSO user object stores roles as [[clientAppId, roleId], ...] pairs.
 * We filter for the selected app, then fetch each matching role's details
 * from client_app_roles/{id} to get name and plugin_feature_permissions.
 *
 * Stores the result in localStorage as "client_app_roles" and returns
 * the merged plugin_feature_permissions.
 *
 * @param {Object} user - The current user object (from SSO)
 * @param {number|string} selectedAppId - The selected client app ID
 * @returns {Promise<Array>} Merged plugin_feature_permissions array
 */
const fetchRolesAndPluginPermissions = async (user, selectedAppId) => {
  try {
    const authToken = sessionStorage.getItem("fmdp-access-token");
    if (!authToken) return [];

    const coreApiUrl = process.env.REACT_APP_CORE_API_URL;

    // Step 1: Fetch the current user's full data from users API.
    // The SSO user object's client_app_roles may not include all assigned roles.
    // The users/{id} API returns roles as [[clientAppId, roleId], ...] pairs
    // which is the authoritative source (same as Edit User form).
    const userId = user?.id;
    if (!userId) return [];

    const userRes = await fetch(`${coreApiUrl}/users/${userId}`, {
      headers: { Authorization: authToken },
    });
    if (!userRes.ok) return [];
    const userData = await userRes.json();

    // Step 2: Filter the user's roles for the selected app
    const userRoleAssignments = userData?.roles || [];
    let selectedAppRoleIds = [];

    // Flat role IDs do not contain their client app association. Fetch the
    // selected app's role catalog first so we can safely scope those IDs.
    if (!userRoleAssignments.some((role) => Array.isArray(role))) {
      const appRolesRes = await fetch(
        `${coreApiUrl}/client_app_roles?client_app_id=${encodeURIComponent(selectedAppId)}&per_page=1000`,
        { headers: { Authorization: authToken } }
      );
      if (appRolesRes.ok) {
        const appRolesData = await appRolesRes.json();
        selectedAppRoleIds = (appRolesData?.data || []).map((role) => role.id);
      }
    }

    const roleIdsForApp = getRoleIdsForSelectedApp(
      userRoleAssignments,
      selectedAppId,
      selectedAppRoleIds,
      userData?.client_ids || []
    );

    if (roleIdsForApp.length === 0) {
      FMDPStorage.set("client_app_roles", []);
      return [];
    }

    // Step 3: Fetch each role's details from client_app_roles/{id} in parallel
    const roleResponses = await Promise.allSettled(
      roleIdsForApp.map((roleId) =>
        fetch(`${coreApiUrl}/client_app_roles/${roleId}`, {
          headers: { Authorization: authToken },
        }).then((res) => (res.ok ? res.json() : null))
      )
    );

    const roleDetails = roleResponses
      .filter((r) => r.status === "fulfilled" && r.value)
      .map((r) => r.value);

    // Step 4: Build and store client_app_roles in localStorage
    const clientAppRolesEntry = {
      client_app_id: selectedAppId,
      roles: roleDetails.map((role) => ({ id: role.id, name: role.name })),
    };
    FMDPStorage.set("client_app_roles", [clientAppRolesEntry]);

    // Step 5: Collect and merge plugin_feature_permissions from all roles
    const allPermissions = roleDetails.flatMap(
      (role) => role.plugin_feature_permissions || []
    );

    if (allPermissions.length === 0) return [];

    // Merge by plugin_feature_id — union of permissions across roles
    const mergedMap = {};
    for (const perm of allPermissions) {
      const pfId = perm.plugin_feature_id;
      if (!mergedMap[pfId]) {
        mergedMap[pfId] = {
          plugin_feature_id: pfId,
          permission_json: {},
        };
      }

      const target = mergedMap[pfId].permission_json;
      const source = perm.permission_json || {};

      for (const featureKey of Object.keys(source)) {
        if (!target[featureKey]) {
          target[featureKey] = {};
        }
        for (const action of Object.keys(source[featureKey])) {
          // Union: true if ANY role grants it
          if (source[featureKey][action]) {
            target[featureKey][action] = true;
          }
        }
      }
    }

    return Object.values(mergedMap);
  } catch (error) {
    console.error("Failed to fetch roles and plugin permissions:", error);
    return [];
  }
};

export const toggleAppInfo = async (user, selectedAppId, clientAppData) => {
  // Find index of selectAppId in user.clients app
  const index = user?.client_apps
    ? user.client_apps.findIndex((app) => app.id == selectedAppId)
    : -1;

  // If user has access to selectedApp
  if (index >= 0) {
    const fullAppName = user.client_apps[index].name;

    // Expected format of name is Client Name - App Name e.g HMS - Management Portal
    // Find the last occurrence of " - " (space-dash-space) to properly split client and app name
    const separatorIndex = fullAppName.lastIndexOf(" - ");

    const clientName = separatorIndex >= 0
      ? fullAppName.substring(0, separatorIndex)
      : "";

    const applicationName = separatorIndex >= 0
      ? fullAppName.substring(separatorIndex + 3) // +3 to skip " - "
      : fullAppName;

    // Remove spaces and dashes to create the correct format
    const applicationNameCorrectFormat = applicationName
      ?.split(/[\s-]+/)
      ?.filter(word => word.length > 0)
      ?.join("") || "";

    // Special handling for Eco-Drive to map to EcoDriveDriverScore
    let finalAppName = applicationNameCorrectFormat;
    if (applicationNameCorrectFormat === "EcoDrive") {
      finalAppName = "EcoDriveDriverScore";
    }

    // Check if FMDP has selected application
    if (FMDP_APPLICATIONS.includes(finalAppName)) {
      const freshConfig = clientAppData?.user_sessions?.client_app?.client?.config_json;
      if (freshConfig != null) {
        const storedUser = FMDPStorage.get("current-user");
        const currentUser = Array.isArray(storedUser?.client_apps) &&
          storedUser.client_apps.some((app) => app.id == selectedAppId)
          ? storedUser
          : user;
        FMDPStorage.set("current-user", {
          ...currentUser,
          client_apps: currentUser.client_apps.map((app) =>
            app.id == selectedAppId ? { ...app, client: { ...app.client, config_json: freshConfig } } : app
          ),
        });
      }
      FMDPStorage.set("app_features", groupedUserFeatures(clientAppData?.app_features));
      FMDPStorage.set("created-by-client-id", clientAppData?.created_by_client_id);
      FMDPStorage.set("created-by-reseller", clientAppData?.created_by_reseller);
      FMDPStorage.set("reseller", clientAppData?.reseller);
      FMDPStorage.set("selected-client", clientName);
      FMDPStorage.set("selected-app", finalAppName);
      FMDPStorage.set("selected-app-id", selectedAppId);
      FMDPStorage.set("selected-client-id", clientAppData?.user_sessions?.client_app?.client?.id);

      // Fetch role details and plugin_feature_permissions from client_app_roles
      // API for each of the user's roles. Also stores client_app_roles in
      // localStorage for runtime use. This data is not available in the
      // sso/select_client_app response — it must be fetched separately.
      const mergedPluginPermissions = await fetchRolesAndPluginPermissions(user, selectedAppId);
      FMDPStorage.set("plugin_features", mergedPluginPermissions);

      return true;
    }
  }

  return false;
};
